import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import './PracticeExam.css';
import studentApi from './StudentApi';
import { FiEdit, FiArrowLeft, FiArrowRight, FiSend, FiBookmark } from 'react-icons/fi';
import Swal from 'sweetalert2';

function PracticeExam() {
    const navigate = useNavigate();
    const location = useLocation();

    // Hooks
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [markedForReview, setMarkedForReview] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [questionTimes, setQuestionTimes] = useState({}); // Track per-question time
    const questionStartRef = useRef(0); // Start time for current question

    const TOTAL_QUESTIONS = questions.length;

    let { examId, subjectId, topicId, questions: passedQuestions, practiceSetId } = location.state || {};
    const examIdRef = useRef(examId);

    // Stopwatch Timer
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const timerRef = useRef(null);
    const [tooltip, setTooltip] = useState("👏 Keep it up!");
    const [colorClass, setColorClass] = useState("tps-timer-green");

    // Fetch questions
    useEffect(() => {
        if (passedQuestions && passedQuestions.length > 0) {
            setQuestions(passedQuestions);
            examIdRef.current = examIdRef.current || passedQuestions[0].exam_id;
            setLoading(false);
            return;
        }

        if (!examIdRef.current && !topicId && !subjectId && !practiceSetId) {
            setError('Missing exam, topic, or practice set information.');
            setLoading(false);
            return;
        }

        const fetchQuestions = async () => {
            try {
                setLoading(true);
                const params = {
                    exam_id: examId || examIdRef.current,
                    subject_id: subjectId || undefined,
                    topic_id: topicId || undefined,
                    voucher_id: location.state?.voucherId || undefined,
                };
                const res = await studentApi.get('/exam-questions', { params });
                const questionsArray = Array.isArray(res.data) ? res.data : Array.isArray(res.data.data) ? res.data.data : [];
                const formatted = questionsArray.map(q => ({
                    id: q.id,
                    exam_id: q.exam_id,
                    question: q.question_text,
                    options: [q.option_a, q.option_b, q.option_c, q.option_d],
                    correct_option_array: q.correct_option_array,
                    correct_option: q.correct_option,
                    type: q.question_type === 'MCQ_RADIO' ? 'single' : 'multiple',
                }));
                setQuestions(formatted);
                if (!examIdRef.current && formatted.length > 0) examIdRef.current = formatted[0].exam_id;
            } catch (err) {
                setError('Failed to load questions. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, [practiceSetId, topicId, subjectId, examId, location.state?.voucherId, passedQuestions]);

    // ✅ Start unlimited stopwatch timer
    useEffect(() => {
        timerRef.current = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, []);

    // ✅ Tooltip and color change logic
    useEffect(() => {
        if (elapsedSeconds < 1800) {
            setTooltip("👏 Keep it up!");
            setColorClass("tps-timer-green");
        } else if (elapsedSeconds < 3600) {
            setTooltip("💡 Try to solve faster.");
            setColorClass("tps-timer-orange");
        } else if (elapsedSeconds < 5400) {
            setTooltip("🔥 Great focus, don’t stop now.");
            setColorClass("tps-timer-red");
        } else {
            setTooltip("⚡ Marathon mode! Stay consistent.");
            setColorClass("tps-timer-purple");
        }
    }, [elapsedSeconds]);

    // Helper: format hh:mm:ss
    const formatTime = (seconds) => {
        const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const s = String(seconds % 60).padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    // LocalStorage key
    const storageKey = `practice-progress-${examIdRef.current || questions[0]?.exam_id}-${topicId}`;

    // Save progress
    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify({ selectedAnswers, elapsedSeconds }));
    }, [selectedAnswers, elapsedSeconds, storageKey]);

    // Load progress
    useEffect(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const { selectedAnswers, elapsedSeconds } = JSON.parse(saved);
            setSelectedAnswers(selectedAnswers || {});
            setElapsedSeconds(elapsedSeconds || 0);
        }
    }, [storageKey]);

    // ✅ Track question start time
    useEffect(() => {
        questionStartRef.current = elapsedSeconds;
    }, [currentIndex]);


    const recordTimeForCurrentQuestion = () => {
        setQuestionTimes(prev => {
            let timeSpent = elapsedSeconds - questionStartRef.current;
            if (timeSpent <= 0) timeSpent = 1; // ✅ fallback to 1 second

            const prevTime = prev[currentIndex] || 0;
            const newTimes = { ...prev, [currentIndex]: prevTime + timeSpent };
            console.log(`🔹 Time recorded for Q${currentIndex + 1}:`, newTimes[currentIndex]);
            return newTimes;
        });

        questionStartRef.current = elapsedSeconds; // reset for next question
    };



    // ✅ FIXED ANSWER CHECK LOGIC
    const attemptedCount = Object.keys(selectedAnswers).length;

    const correctCount = questions.reduce((acc, q, idx) => {
        const sel = selectedAnswers[idx];
        if (!sel) return acc;

        if (q.type === 'single') {
            return sel === q.correct_option ? acc + 1 : acc;
        }

        if (q.type === 'multiple') {
            const correctOptions = q.correct_option_array || [];
            const selectedOptions = Array.isArray(sel) ? sel : [];
            return JSON.stringify(correctOptions.sort()) === JSON.stringify(selectedOptions.sort()) ? acc + 1 : acc;
        }

        return acc;
    }, 0);

    const wrongCount = attemptedCount - correctCount;
    const unattemptedCount = TOTAL_QUESTIONS - attemptedCount;
    const percentage = ((correctCount / TOTAL_QUESTIONS) * 100).toFixed(2);

    const formatDateTime = (date) => {
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
            `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const submitExamNow = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            // Compute final question times including last question
            const finalQuestionTimes = { ...questionTimes };
            const lastIndex = currentIndex;
            const timeSpentForLast = elapsedSeconds - questionStartRef.current;
            finalQuestionTimes[lastIndex] = (finalQuestionTimes[lastIndex] || 0) + (timeSpentForLast > 0 ? timeSpentForLast : 1);

            const studentData = JSON.parse(localStorage.getItem('studentData') || '{}');
            const studentId = parseInt(studentData.id, 10);
            if (!studentId) {
                Swal.fire('Error', 'Student ID missing.', 'error');
                navigate('/student-login');
                return;
            }

            // ✅ ADD THIS
            const totalScore = correctCount;
            const totalMarks = TOTAL_QUESTIONS;

            const answersPayload = questions.map((q, idx) => {
                const sel = selectedAnswers[idx];
                return q.type === 'single'
                    ? { question_id: q.id, selected_option: sel || null, selected_options: [] }
                    : { question_id: q.id, selected_option: null, selected_options: Array.isArray(sel) ? sel.map(String) : [] };
            });

            const payload = {
                student_id: studentId,
                exam_id: examIdRef.current,
                subject_id: subjectId,
                topic_id: topicId,
                source_type: 'practice',
                duration_taken: elapsedSeconds,
                answers: answersPayload,
                started_at: formatDateTime(new Date(Date.now() - elapsedSeconds * 1000)),
                submitted_at: formatDateTime(new Date()),
                score_obtained: totalScore,
                attempted_questions: attemptedCount,
                unattempted_questions: unattemptedCount,
                correct_answers: correctCount,
                wrong_answers: wrongCount,
                total_marks: totalMarks,
                total_questions: TOTAL_QUESTIONS,
                percentage: ((totalScore / totalMarks) * 100).toFixed(2),
                passed: ((totalScore / totalMarks) * 100) >= 40 ? 1 : 0,
                answered_json: JSON.stringify(selectedAnswers),
            };

            console.log("✅ Final Payload:", payload); //  Debug line
            console.log("🧾 Payload sent to backend:", JSON.stringify(payload, null, 2));

            const res = await studentApi.post('/practice/store-result', payload);
            const resultId = res.data?.data?.id;
            if (!resultId) throw new Error('Result ID missing');

            await saveQuestionTimes(resultId, finalQuestionTimes);

            Swal.fire({
                icon: 'success',
                title: 'Exam Submitted!',
                text: `Total Marks: ${TOTAL_QUESTIONS}, Correct: ${correctCount}, Wrong: ${wrongCount}`,
            }).then(() => {
                navigate('/practice-result', {
                    state: {
                        totalQuestions: TOTAL_QUESTIONS,
                        attempted: attemptedCount,
                        unattempted: TOTAL_QUESTIONS - attemptedCount,
                        correct: correctCount,
                        wrong: wrongCount,
                        score: percentage,
                        totalDuration: formatTime(elapsedSeconds),
                        timeTaken: formatTime(elapsedSeconds),
                        selectedAnswers,
                        questions,
                        questionTimes: finalQuestionTimes,
                        examId: examIdRef.current,
                        topicId,
                        subjectId,
                        started_at: formatDateTime(new Date(Date.now() - elapsedSeconds * 1000)),  // ✅ add
                        submitted_at: formatDateTime(new Date()),
                    }
                });
            });

        } catch (err) {
            console.error(err);
            Swal.fire('Error', err.message || 'Failed to submit exam', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };



    const saveQuestionTimes = async (resultId, times) => {
        console.log("🔹 saveQuestionTimes called", { resultId, times, questions });

        if (!questions || !times || !resultId) {
            console.warn("⚠️ Missing data, aborting saveQuestionTimes");
            return;
        }

        const payload = {
            result_id: resultId,
            exam_id: examIdRef.current,
            subject_id: subjectId || null, // ✅ fallback
            topic_id: topicId || null,     // ✅ fallback
            question_times: questions.map((q, idx) => ({
                question_id: q.id,
                question_index: idx,
                time_spent: times[idx] || 1, // ✅ safer fallback
            }))
        };

        console.log("🔹 Payload being sent to backend:", payload);

        try {
            const res = await studentApi.post('/practice-question-time', payload);
            console.log('✅ Question times saved', res.data);
        } catch (err) {
            console.error('❌ Error saving question times', err.response?.data || err.message);
        }
    };



    return (
        <MathJaxContext>
            <div className="practice-exam-container">
                {loading ? (
                    <p>Loading questions...</p>
                ) : error ? (
                    <p style={{ color: 'red' }}>{error}</p>
                ) : questions.length === 0 ? (
                    <p>No questions found for this exam.</p>
                ) : (
                    <div className="exam-layout">
                        <div className="exam-main">
                            <div className="exam-header">
                                <h2 className="practice-exam-title">
                                    <FiEdit size={30} className="title-icon" /> Practice Exam
                                </h2>
                                <div className={`tps-timer-box ${colorClass}`}>
                                    <div className="tps-timer-label">
                                        <span className="tps-live-dot"></span> ⏱ Time Elapsed
                                    </div>
                                    <span className="tps-timer-digits">{formatTime(elapsedSeconds)}</span>
                                </div>
                            </div>

                            <div className="question-box">
                                <div className="question-content">
                                    <h3>Q{currentIndex + 1}:</h3>
                                    <MathJax>
                                        <div dangerouslySetInnerHTML={{ __html: questions[currentIndex]?.question || '' }} />
                                    </MathJax>
                                </div>

                                <div className="options">
                                    {questions[currentIndex]?.options?.map((opt, idx) => {
                                        const type = questions[currentIndex]?.type;
                                        const name = `question-${currentIndex}`;
                                        const optionLetter = String.fromCharCode(65 + idx);
                                        const isSelected =
                                            type === "single"
                                                ? selectedAnswers[currentIndex] === optionLetter
                                                : selectedAnswers[currentIndex]?.includes(optionLetter);

                                        const handleChange = e => {
                                            if (type === 'single') {
                                                setSelectedAnswers({ ...selectedAnswers, [currentIndex]: e.target.value });
                                            } else {
                                                const existing = selectedAnswers[currentIndex] || [];
                                                setSelectedAnswers({
                                                    ...selectedAnswers,
                                                    [currentIndex]: e.target.checked
                                                        ? [...existing, e.target.value]
                                                        : existing.filter(item => item !== e.target.value)
                                                });
                                            }
                                        };

                                        return (
                                            <label key={idx} className={`option-button ${isSelected ? 'selected' : ''}`}>
                                                <input
                                                    type={type === 'single' ? 'radio' : 'checkbox'}
                                                    name={name}
                                                    value={optionLetter}
                                                    checked={isSelected}
                                                    onChange={handleChange}
                                                    className="visible-input"
                                                />
                                                <MathJax>
                                                    <span>
                                                        <strong>{optionLetter}.</strong>{' '}
                                                        <span dangerouslySetInnerHTML={{ __html: opt }} />
                                                    </span>
                                                </MathJax>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="navigation-buttons">
                                <button
                                    onClick={() => {
                                        // Record last question time synchronously
                                        const lastIndex = currentIndex;
                                        const timeSpentForLast = elapsedSeconds - questionStartRef.current;
                                        setQuestionTimes(prev => {
                                            const prevTime = prev[lastIndex] || 0;
                                            return { ...prev, [lastIndex]: prevTime + (timeSpentForLast > 0 ? timeSpentForLast : 1) };
                                        });

                                        setCurrentIndex(i => i - 1);
                                    }}
                                    disabled={currentIndex === 0}
                                >
                                    <FiArrowLeft /> Previous
                                </button>
                                <button
                                    onClick={() => {
                                        recordTimeForCurrentQuestion(); // ✅ Record time before moving
                                        setCurrentIndex(i => i + 1);
                                    }}
                                    disabled={currentIndex === TOTAL_QUESTIONS - 1}
                                >
                                    Next <FiArrowRight />
                                </button>
                                <button
                                    onClick={() => setMarkedForReview(prev => ({ ...prev, [currentIndex]: !prev[currentIndex] }))}
                                    className={`marked-review-button ${markedForReview[currentIndex] ? 'marked' : ''}`}
                                >
                                    <FiBookmark /> {markedForReview[currentIndex] ? 'Unmark Review' : 'Mark for Review'}
                                </button>
                                <button onClick={submitExamNow} className='submit-button' disabled={isSubmitting}>
                                    <FiSend /> Submit
                                </button>
                            </div>

                            {/* Progress */}
                            <div className="progress-chart">
                                <div className="bar-container">
                                    <div
                                        className="bar-fill-attempted"
                                        style={{ width: `${(Object.keys(selectedAnswers).length / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                    <div
                                        className="bar-fill-unattempted"
                                        style={{ width: `${((TOTAL_QUESTIONS - Object.keys(selectedAnswers).length) / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                </div>
                            </div>
                            <div className="progress-labels">
                                <span>✅ Attempted: {Object.keys(selectedAnswers).length}</span>
                                <span>❌ Unattempted: {TOTAL_QUESTIONS - Object.keys(selectedAnswers).length}</span>
                            </div>
                        </div>

                        {/* Sidebar */}
                       <div className="exam-sidebar">
   <div className='tracker-box-container'>
     <div className="tracker-legend">
        <div className="legend-item"><div className="legend-box answered"></div> Answered</div>
        <div className="legend-item"><div className="legend-box unanswered"></div> Not Answered</div>
        <div className="legend-item"><div className="legend-box not-visited"></div> Not Visited</div>
        <div className="legend-item"><div className="legend-box review"></div> Mark for Review</div>
    </div>
   </div>

    {questions.length > 0 && (
        <div className="question-tracker">
            {questions.map((_, i) => {
                const isCurrent = currentIndex === i;
                const hasAnswered = selectedAnswers[i];
                const wasVisited = i <= currentIndex;
                const isReviewMarked = markedForReview[i];
                const status = isReviewMarked
                    ? 'review'
                    : hasAnswered
                        ? 'answered'
                        : wasVisited
                            ? 'unanswered'
                            : 'not-visited';
                return (
                    <div
                        key={i}
                        className={`tracker-box ${status} ${isCurrent ? 'current' : ''}`}
                        onClick={() => {
                            recordTimeForCurrentQuestion();
                            setCurrentIndex(i);
                        }}
                        title={`Q${i + 1} — ${status.replace('-', ' ')}`}
                    >
                        {i + 1}
                    </div>
                );
            })}
        </div>
    )}
</div>

                    </div>
                )}
            </div>
        </MathJaxContext>
    );
}

export default PracticeExam;
